import React, { useCallback, memo, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { IS_NATIVE, IS_SERVER } from '@r3l/common';
import { goToProfile, goToUrl, goToTopic } from 'modules/navigation/navigation.actions';
import nativeStyles from 'styles/markdown.native';
import { useColorMode } from 'styles/ColorMode';
import MarkdownNative from 'react-native-markdown-display';
import { Image, BodyText, InlineLink } from 'modules/styled';

MD.propTypes = {
  markdown: PropTypes.string,
  noLink: PropTypes.bool,
  style: PropTypes.object,
  isPreview: PropTypes.bool,
};

export const Markdown = memo(MD);
export default Markdown;

export function MD({ markdown, noLink, style, isPreview }) {
  const processNativeLink = useNativeLink(noLink);
  const MarkdownLink = getMDLink(noLink);
  const [mode, , getColor] = useColorMode();

  const colors = useMemo(
    () => ({
      black: getColor('black'),
      blue: getColor('blue'),
    }),
    [mode, getColor] // eslint-disable-line
  );

  if (IS_SERVER) return <BodyText>{markdown}</BodyText>;

  const RENDERERS = {
    ...(isPreview
      ? {
          image: (img) => {
            if (!img.attributes.src) return null;
            return (
              <Image
                key={img.attributes.src}
                sx={{ width: 100, height: 100 }}
                source={{ uri: img.attributes.src }}
                resizeMode={'contain'}
              />
            );
            // return (
            //   <Text>
            //     {'['}Click to see Image...{']'}
            //   </Text>
            // );
          },
        }
      : {}),
    link: (node, children) => (
      <MarkdownLink key={node?.attributes?.href} href={node?.attributes?.href}>
        {children}
      </MarkdownLink>
    ),
    blocklink: (node, children) => (
      <MarkdownLink key={node?.attributes?.href} href={node?.attributes?.href}>
        {children}
      </MarkdownLink>
    ),
  };

  return (
    <MarkdownNative
      mergeStyle={true}
      onLinkPress={processNativeLink}
      style={nativeStyles(colors, style)}
      rules={
        IS_NATIVE ? { ...(RENDERERS.image ? { image: RENDERERS.image } : {}) } : RENDERERS
      }
    >
      {markdown}
    </MarkdownNative>
  );
}

function useNativeLink(noLink) {
  const dispatch = useDispatch();
  return useCallback(
    (url) => {
      if (noLink) return null;
      switch (true) {
        case url === '':
          return true;
        case /__user_profile__/.test(url):
          return dispatch(goToProfile(url.replace('__user_profile__', '')));
        case /__tag_link__/.test(url):
          return dispatch(goToTopic(url.replace('__tag_link__', '')));
        default:
          return dispatch(goToUrl(url));
      }
    },
    [dispatch, noLink]
  );
}

function getMDLink(noLink) {
  MarkdownLink.propTypes = {
    href: PropTypes.string,
    children: PropTypes.node,
    onPress: PropTypes.func,
  };
  function MarkdownLink({ href, children, onPress, ...rest }) {
    if (href === '') return children;
    const regex = /^(https?|file|ftps?|mailto|javascript|data:image\/[^;]{2,9};):/i;
    const isAbsolute = regex.test(href);
    return (
      <InlineLink
        to={href}
        {...rest}
        sx={{ overflowWrap: 'break-word', wordBreak: 'break-all' }}
        webAction={(e) => (noLink ? e.preventDefault() : e.stopPropagation())}
        nativeAction={(e) => (noLink ? e.preventDefault() : onPress(e))}
        external={isAbsolute}
        target={isAbsolute ? '_blank' : null}
      >
        {children}
      </InlineLink>
    );
  }
  return memo(MarkdownLink);
}
